import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Box, Paper, Typography, Grid, CircularProgress, Chip, Alert } from '@mui/material';
import { toast } from 'react-toastify';
import { PhotoLibrary as PhotoLibraryIcon, Check as CheckIcon, PictureAsPdf as PictureAsPdfIcon } from '@mui/icons-material';
import { useMonitoringReportShared } from '../../context/MonitoringReportContext';
import { usePhotoState, usePhotoUploads } from '../../photos/hooks';
import DraggablePhotoItem from '../../photos/components/DraggablePhotoItem';
import UploadingPhotoItem from '../../photos/components/UploadingPhotoItem';
import UploadDropzoneCard from '../../photos/components/UploadDropzoneCard';
import PhotoGridHeader from '../../photos/components/PhotoGridHeader';
import SiteConditionsSection from './SiteConditionsSection';
import { ReportPhotoDTO, BatchPhotoUpdateDTO, PhotoUploadResponse } from '../../photos/types';
import { SiteConditionsDTO } from '../../types/monitoring-report.types';
import { formatISODate } from 'common/utils/common.util';
import { parseISO, isValid } from 'date-fns';
import { Button } from 'components/index';
import { Analytics, ANALYTICS_EVENTS } from '../../../../../services/analyticsService';

interface UploadImagesStepProps {
  onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void;
  onPhotosChange?: (photos: any[]) => void;
  isReadOnly?: boolean;
}

const UploadImagesStep: React.FC<UploadImagesStepProps> = ({ onUnsavedChangesChange, onPhotosChange, isReadOnly = false }) => {
  const {
    reportState,
    setReportState,
    uploadingPhotos,
    setUploadingPhotos,
    setHasUnsavedPhotoChanges,
    registerPhotoSaveFunction,
    api,
    loadPhotos,
    photos,
    setPhotos,
    photoUrls,
    setPhotoUrls,
    photosLoaded,
    setIsDirty,
    reportType,
    saveReport,
    isDirty,
    navigationGuardRef,
  } = useMonitoringReportShared() as any;

  const reportId = reportState.reportId;
  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [photosPerRow, setPhotosPerRow] = useState(3);
  const photoRefs = useRef<{ [key: string]: () => void }>({});

  // Cover page photo selection state
  const [isSelectingForCoverPage, setIsSelectingForCoverPage] = useState(false);
  const [tempSelectedPhotoIds, setTempSelectedPhotoIds] = useState<string[]>([]);
  const [isExportingPhotos, setIsExportingPhotos] = useState(false);

  useEffect(() => {
    setHasUnsavedPhotoChanges(false);
    onUnsavedChangesChange?.(false);
  }, [setHasUnsavedPhotoChanges]);

  const handleUnsaved = useCallback(
    (has: boolean) => {
      setHasUnsavedPhotoChanges(has);
      onUnsavedChangesChange?.(has);
    },
    [setHasUnsavedPhotoChanges, onUnsavedChangesChange],
  );

  const {
    photos: statePhotos,
    localPhotoStates,
    photoUrls: statePhotoUrls,
    isLoading,
    isSaving,
    hasUnsavedChanges,
    handleCaptionChange,
    handleUpdateCaption,
    handleDeletePhoto: originalHandleDeletePhoto,
    handleSaveChanges,
    handleCancelChanges,
    handleRotatePhoto,
    handleSaveRotation,
    savingRotationIds,
    movePhoto,
    mergeUploadedPhoto,
    mergeEditedPhoto,
    sortPhotosNow,
  } = usePhotoState<ReportPhotoDTO>({
    reportId,
    photoContext: {
      photos,
      photoUrls,
      photosLoaded,
      uploadingPhotos,
      setUploadingPhotos,
      setPhotos,
      setPhotoUrls,
      loadPhotos,
    },
    batchUpdatePhotos: api.batchUpdatePhotos as (reportId: string, updates: BatchPhotoUpdateDTO) => Promise<void>,
    deletePhoto: api.deletePhoto as (reportId: string, photoId: string) => Promise<void>,
    getPhotoUrl: api.getPhotoUrl as (blobName: string) => Promise<string>,
    getImageAsBase64: api.getImageAsBase64 as (blobName: string) => Promise<string>,
    updateEditedPhoto: api.updateEditedPhoto as (reportId: string, photoId: string, blob: Blob) => Promise<any>,
    onPhotosChange,
    onUnsavedChangesChange: handleUnsaved,
  });

  // Wrap handleDeletePhoto to also remove from coverPagePhotoIds
  const handleDeletePhoto = useCallback(
    async (photoId: string) => {
      await originalHandleDeletePhoto(photoId);

      // Remove from coverPagePhotoIds if it's selected for cover page
      const coverPagePhotoIds = reportState.coverPagePhotoIds || [];
      if (coverPagePhotoIds.includes(photoId)) {
        const updatedPhotoIds = coverPagePhotoIds.filter((id: string) => id !== photoId);
        setReportState((prev: any) => {
          // Update both coverPagePhotoIds and coverPage.coverPagePhotoIds
          const updatedCoverPage = prev.coverPage
            ? { ...prev.coverPage, coverPagePhotoIds: updatedPhotoIds }
            : { coverPagePhotoIds: updatedPhotoIds };

          return {
            ...prev,
            coverPagePhotoIds: updatedPhotoIds,
            coverPage: updatedCoverPage,
          };
        });
        setIsDirty(true);
      }

      // Remove from temp selection if in selection mode
      if (isSelectingForCoverPage && tempSelectedPhotoIds.includes(photoId)) {
        setTempSelectedPhotoIds(prev => prev.filter((id: string) => id !== photoId));
      }
    },
    [originalHandleDeletePhoto, reportState.coverPagePhotoIds, isSelectingForCoverPage, tempSelectedPhotoIds, setReportState, setIsDirty],
  );

  const handleExportPhotos = useCallback(async () => {
    if (!reportId || !api.buildPhotosOnlyHtml || !api.exportPdf) return;
    setIsExportingPhotos(true);
    try {
      if (hasUnsavedChanges && handleSaveChanges) {
        await handleSaveChanges();
      }

      const projectName = reportState.projectSummary?.projectTitle || reportState.projectSummary?.projectNumber || '';

      // Use the Site Conditions "Inspection Date" for the photos-only export heading.
      // parseISO interprets yyyy-MM-dd as local midnight, avoiding the UTC-to-local
      // timezone shift that new Date('yyyy-MM-dd') would cause (off by one day for
      // users west of UTC).
      const siteConditionsInspectionDate = reportState.siteConditions?.inspectionDate;
      const parsedInspectionDate = siteConditionsInspectionDate ? parseISO(siteConditionsInspectionDate) : null;
      const headingDate = parsedInspectionDate && isValid(parsedInspectionDate) ? parsedInspectionDate : new Date();

      const html = await api.buildPhotosOnlyHtml(reportId, {
        projectName,
        date: headingDate,
      });
      const { blob } = await api.exportPdf(html, false);
      const projNumber = String(reportState.projectSummary?.projectNumber ?? '').trim();
      const projName = String(reportState.projectSummary?.projectTitle ?? '').trim();
      const payappNumber = String(reportState.siteConditions?.siteVisit ?? '').trim();
      const sanitize = (s: string) =>
        s
          .replace(/[\\/:*?"<>|]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
      const fileName =
        [sanitize(projNumber), sanitize(projName), 'Photos for Report', payappNumber ? `#${sanitize(payappNumber)}` : '']
          .filter(Boolean)
          .join(' ')
          .trim() || 'Photographs_export';
      const safeFileName = `${fileName}.pdf`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = safeFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      Analytics.track(ANALYTICS_EVENTS.EXPORT_PHOTOS, {
        project_id: String(reportState.projectSummary?.id ?? ''),
        project_label: `${reportState.projectSummary?.projectNumber ?? ''} - ${reportState.projectSummary?.projectTitle ?? ''}`.trim(),
        report_type: reportState.reportType,
      });
      toast.success('Photos exported successfully.');
    } catch (err) {
      console.error('Export photos failed:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to export photos.');
    } finally {
      setIsExportingPhotos(false);
    }
  }, [
    reportId,
    api.buildPhotosOnlyHtml,
    api.exportPdf,
    hasUnsavedChanges,
    handleSaveChanges,
    reportState.projectSummary?.projectNumber,
    reportState.projectSummary?.projectTitle,
    reportState.drawSummary?.applicationNumber,
    reportState.siteConditions?.inspectionDate,
  ]);

  const {
    handleFilesSelected,
    movePhoto: moveUploadingPhoto,
    removeUploadingPhoto,
  } = usePhotoUploads({
    reportId,
    photoContext: { uploadingPhotos, setUploadingPhotos },
    uploadPhoto: api.uploadPhoto as (reportId: string, file: File, onProgress?: (p: number) => void) => Promise<PhotoUploadResponse>,
    // Each uploaded photo is merged and sorted once inside `mergeUploadedPhoto`.
    // We keep `onBatchComplete` for a single final pass after the whole batch.
    onPhotoUploaded: mergeUploadedPhoto,
    onBatchComplete: sortPhotosNow,
    getBaseIndex: () => {
      if (statePhotos.length === 0) return 0;
      const maxOrder = Math.max(...statePhotos.map(p => p.sortOrder));
      return maxOrder + 1;
    },
  });

  useEffect(() => {
    registerPhotoSaveFunction(handleSaveChanges);
  }, [handleSaveChanges, registerPhotoSaveFunction]);

  // Fire save-draft if the report or photo captions have unsaved changes.
  // Called without await so the file-picker dialog can open immediately
  // (browsers require the dialog to be triggered synchronously from the user gesture).
  const triggerSaveIfNeeded = useCallback(() => {
    if (isDirty || hasUnsavedChanges) {
      saveReport();
    }
  }, [isDirty, hasUnsavedChanges, saveReport]);

  const triggerFileInput = useCallback(() => {
    triggerSaveIfNeeded();
    fileInputRef.current?.click();
  }, [triggerSaveIfNeeded]);
  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length) handleFilesSelected(files);
    e.currentTarget.value = '';
  };

  const onEditPhoto = useCallback(
    async (photoId: string, editedBlob: Blob) => {
      const updated = await api.updateEditedPhoto(reportId, photoId, editedBlob);
      await mergeEditedPhoto(updated);
    },
    [api, reportId, mergeEditedPhoto],
  );

  const handleRotate = useCallback(
    (photoId: string) => {
      handleRotatePhoto(photoId);
    },
    [handleRotatePhoto],
  );

  const handleSaveRotationForPhoto = useCallback(
    (photoId: string): Promise<void> => {
      return handleSaveRotation(photoId);
    },
    [handleSaveRotation],
  );

  // Sort photos: ready-to-caption photos (with URLs) at top, loading photos (without URLs) at bottom
  const sortedPhotos = useMemo(() => {
    const readyPhotos: ReportPhotoDTO[] = [];
    const loadingPhotos: ReportPhotoDTO[] = [];

    statePhotos.forEach(photo => {
      const hasUrl = statePhotoUrls[photo.id] && statePhotoUrls[photo.id].trim() !== '';
      if (hasUrl) {
        readyPhotos.push(photo);
      } else {
        loadingPhotos.push(photo);
      }
    });

    // Return ready photos first, then loading photos
    return [...readyPhotos, ...loadingPhotos];
  }, [statePhotos, statePhotoUrls]);

  const handleMoveToNext = useCallback(
    (currentIndex: number) => {
      const nextIndex = currentIndex + 1;
      if (nextIndex < sortedPhotos.length) {
        const nextPhoto = sortedPhotos[nextIndex];
        const trigger = nextPhoto ? photoRefs.current[nextPhoto.id] : undefined;
        if (trigger) {
          trigger();
        }
      }
    },
    [sortedPhotos],
  );

  const handleMoveToPrevious = useCallback(
    (currentIndex: number) => {
      const prevIndex = currentIndex - 1;
      if (prevIndex >= 0) {
        const prevPhoto = sortedPhotos[prevIndex];
        const trigger = prevPhoto ? photoRefs.current[prevPhoto.id] : undefined;
        if (trigger) {
          trigger();
        }
      }
    },
    [sortedPhotos],
  );

  // Create a map for quick lookup of photo index in original array
  const originalIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    statePhotos.forEach((photo, index) => {
      map.set(photo.id, index);
    });
    return map;
  }, [statePhotos]);

  const handleMoveToBottom = useCallback(
    (photoId: string) => {
      const originalIndex = originalIndexMap.get(photoId);
      if (originalIndex === undefined) return;
      movePhoto(originalIndex, statePhotos.length - 1);
    },
    [originalIndexMap, movePhoto, statePhotos.length],
  );

  // Wrapper for movePhoto that converts sorted indices to original array indices
  const handleMovePhoto = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const draggedPhoto = sortedPhotos[dragIndex];
      const hoverPhoto = sortedPhotos[hoverIndex];

      if (!draggedPhoto || !hoverPhoto) return;

      const originalDragIndex = originalIndexMap.get(draggedPhoto.id);
      const originalHoverIndex = originalIndexMap.get(hoverPhoto.id);

      if (originalDragIndex === undefined || originalHoverIndex === undefined) return;

      movePhoto(originalDragIndex, originalHoverIndex);
    },
    [sortedPhotos, originalIndexMap, movePhoto],
  );

  const handleStartCoverPageSelection = useCallback(() => {
    setIsSelectingForCoverPage(true);
    setTempSelectedPhotoIds(reportState.coverPagePhotoIds || []);
  }, [reportState.coverPagePhotoIds]);

  const handleCancelCoverPageSelection = useCallback(() => {
    setIsSelectingForCoverPage(false);
    setTempSelectedPhotoIds([]);
  }, []);

  const handleFinishCoverPageSelection = useCallback(() => {
    // Flush synchronously so reportState/isDirty are committed (and the context's
    // internal refs re-synced) before any code immediately after this call - e.g. the
    // navigation guard below - tries to persist the report.
    flushSync(() => {
      setReportState((prev: any) => {
        // Update both coverPagePhotoIds and coverPage.coverPagePhotoIds
        const updatedCoverPage = prev.coverPage
          ? { ...prev.coverPage, coverPagePhotoIds: tempSelectedPhotoIds }
          : { coverPagePhotoIds: tempSelectedPhotoIds };

        return {
          ...prev,
          coverPagePhotoIds: tempSelectedPhotoIds,
          coverPage: updatedCoverPage,
        };
      });
      setIsDirty(true);
    });
    setIsSelectingForCoverPage(false);
  }, [tempSelectedPhotoIds, setReportState, setIsDirty]);

  // Auto-confirm the in-progress cover page selection when the user navigates away
  // (Next button or clicking another step in the wizard stepper) instead of clicking Done.
  // Saves directly rather than relying on the wizard's own save-before-navigate check,
  // since that check closes over the isDirty value from before this guard runs and would
  // otherwise skip saving when nothing else on the step was dirty.
  useEffect(() => {
    if (!navigationGuardRef) return;

    navigationGuardRef.current = async () => {
      if (isSelectingForCoverPage) {
        handleFinishCoverPageSelection();
        await saveReport();
      }
      return true;
    };

    return () => {
      navigationGuardRef.current = null;
    };
  }, [isSelectingForCoverPage, handleFinishCoverPageSelection, navigationGuardRef, saveReport]);

  const handlePhotoClickForCoverPage = useCallback(
    (photoId: string) => {
      if (!isSelectingForCoverPage) return;

      setTempSelectedPhotoIds(prev => {
        if (prev.includes(photoId)) {
          // Deselect
          return prev.filter(id => id !== photoId);
        } else if (prev.length < 3) {
          // Select (max 3)
          return [...prev, photoId];
        }
        return prev;
      });
    },
    [isSelectingForCoverPage],
  );

  const getPhotoSelectionNumber = useCallback(
    (photoId: string) => {
      const index = tempSelectedPhotoIds.indexOf(photoId);
      return index !== -1 ? index + 1 : null;
    },
    [tempSelectedPhotoIds],
  );

  // Handle site conditions changes and sync to executive summary
  const handleSiteConditionsChange = useCallback(
    (newSiteConditions: SiteConditionsDTO) => {
      setReportState((prev: any) => {
        // Update siteConditions in state
        const updated = {
          ...prev,
          siteConditions: newSiteConditions,
        };

        // If executive summary exists, update it with site conditions (dates formatted for display)
        if (updated.summary?.executiveSummary && (reportType === 'magic' || reportType === 'limited')) {
          const execSummary = updated.summary.executiveSummary;
          const inspectionDateFormatted = newSiteConditions.inspectionDate ? formatISODate(newSiteConditions.inspectionDate) : '';
          const reportDateFormatted = newSiteConditions.reportDate ? formatISODate(newSiteConditions.reportDate) : '';
          const escapeHtml = (value: string) =>
            value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

          // Replace site conditions fields: Site Visit Number and Report Date on same line; remove legacy/standalone Report Date line
          const siteVisitLine = `<p>Site Visit Number: ${escapeHtml(newSiteConditions.siteVisit || '')}${
            reportDateFormatted ? `, ${escapeHtml(reportDateFormatted)}` : ''
          }</p>`;
          const updatedExecSummary = execSummary
            .replace(/<p>Site Visit:.*?<\/p>/, `<p>Site Visit: ${escapeHtml(newSiteConditions.siteVisit || '')}</p>`)
            .replace(/<p>Site Visit Number:.*?<\/p>/, siteVisitLine)
            .replace(/<p>Report Date:.*?<\/p>/, '')
            .replace(
              /<p>Inspection Date:.*?<\/p>/,
              inspectionDateFormatted ? `<p>Inspection Date: ${escapeHtml(inspectionDateFormatted)}</p>` : '',
            )
            .replace(/<p>Conditions:.*?<\/p>/, `<p>Conditions: ${escapeHtml(newSiteConditions.conditions || '')}</p>`)
            .replace(/<p>Inspection Time:.*?<\/p>/, `<p>Inspection Time: ${escapeHtml(newSiteConditions.inspectionTime || '')}</p>`)
            .replace(/<p>Temperature:.*?<\/p>/, `<p>Temperature: ${escapeHtml(newSiteConditions.temperature || '')}</p>`);

          updated.summary = {
            ...updated.summary,
            executiveSummary: updatedExecSummary,
          };
        }

        return updated;
      });
      setIsDirty(true);
    },
    [setReportState, setIsDirty, reportType],
  );

  if (!reportId) return <div>Loading...</div>;
  if (isLoading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='200px'>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Site Conditions Section - For Magic and Limited Reports */}
      {!isReadOnly && (reportType === 'magic' || reportType === 'limited') && (
        <SiteConditionsSection siteConditions={reportState.siteConditions || {}} onChange={handleSiteConditionsChange} />
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant='h5'>Photo Management</Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Export Photos - available for both Limited and Magic reports */}
          {api.buildPhotosOnlyHtml && api.exportPdf && (
            <Button
              variant='outlined'
              startIcon={isExportingPhotos ? <CircularProgress size={18} /> : <PictureAsPdfIcon />}
              onClick={handleExportPhotos}
              disabled={isExportingPhotos || uploadingPhotos.length > 0}
            >
              {isExportingPhotos ? 'Exporting…' : 'Export Photos'}
            </Button>
          )}
          {/* Cover Page Selection - Only for Magic Reports */}
          {!isReadOnly && reportType === 'magic' && statePhotos.length > 0 && (
            <>
              {!isSelectingForCoverPage ? (
                <Button
                  variant='contained'
                  startIcon={<PhotoLibraryIcon />}
                  onClick={handleStartCoverPageSelection}
                  sx={{ color: 'white !important' }}
                >
                  Select for Cover Page
                </Button>
              ) : (
                <>
                  <Chip
                    label={`${tempSelectedPhotoIds.length} / 3 selected`}
                    color={tempSelectedPhotoIds.length > 0 ? 'primary' : 'default'}
                  />
                  <Button
                    variant='outlined'
                    color='error'
                    onClick={handleCancelCoverPageSelection}
                    sx={{
                      color: 'error.main',
                      borderColor: 'error.main',
                      '&:hover': {
                        backgroundColor: 'error.dark',
                        color: 'white !important',
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant='contained'
                    startIcon={<CheckIcon />}
                    onClick={handleFinishCoverPageSelection}
                    sx={{ color: 'white !important' }}
                  >
                    Done
                  </Button>
                </>
              )}
            </>
          )}
        </Box>
      </Box>

      {isSelectingForCoverPage && (
        <Alert severity='info' sx={{ mb: 2 }}>
          Click on photos to select them for the cover page (up to 3). Photos will be numbered in order of selection.
        </Alert>
      )}

      <Paper sx={{ p: 3, backgroundColor: 'white' }}>
        <Box ref={containerRef} sx={{ minHeight: '400px' }}>
          <input type='file' ref={fileInputRef} multiple accept='image/*,.heic' style={{ display: 'none' }} onChange={onFileInputChange} />

          <PhotoGridHeader
            photosCount={statePhotos.length}
            uploadingCount={uploadingPhotos.length}
            failedCount={uploadingPhotos.filter((p: any) => p.status === 'error').length}
            photosWithoutCaptionsCount={statePhotos.filter((p: any) => !p.hasCaption).length}
            hasUnsavedChanges={hasUnsavedChanges}
            isSaving={isSaving}
            photosPerRow={photosPerRow}
            onSaveChanges={handleSaveChanges}
            onCancelChanges={handleCancelChanges}
            onPhotosPerRowChange={setPhotosPerRow}
            isReadOnly={isReadOnly}
            onUploadClick={triggerFileInput}
            onScrollToBottom={() => containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })}
          />

          <Grid container spacing={2}>
            {sortedPhotos.map((photo: ReportPhotoDTO, index: number) => {
              const localState = localPhotoStates[photo.id];
              const baseUrl = statePhotoUrls[photo.id] || '';
              const sep = baseUrl && baseUrl.includes('?') ? '&' : '?';
              const cbUrl = baseUrl ? `${baseUrl}${sep}cb=${encodeURIComponent(photo.updatedOn || photo.id)}` : '';
              const selectionNumber = getPhotoSelectionNumber(photo.id);

              return (
                <Grid size={{ xs: 12, sm: 6, md: 12 / photosPerRow }} key={photo.id}>
                  <Box
                    sx={{
                      position: 'relative',
                    }}
                  >
                    {/* Selection circle in top-left */}
                    {isSelectingForCoverPage && (
                      <Box
                        onClick={e => {
                          e.stopPropagation();
                          handlePhotoClickForCoverPage(photo.id);
                        }}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          zIndex: 11,
                          backgroundColor: selectionNumber !== null ? 'primary.main' : 'rgba(255, 255, 255, 0.9)',
                          color: 'white !important',
                          borderRadius: '50%',
                          width: 36,
                          height: 36,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold',
                          fontSize: '1.1rem',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                          border: '3px solid',
                          borderColor: selectionNumber !== null ? 'white' : 'primary.main',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: 'scale(1.1)',
                          },
                        }}
                      >
                        {selectionNumber || ''}
                      </Box>
                    )}

                    {/* Wrapper to intercept clicks in selection mode */}
                    <Box
                      onClick={
                        isSelectingForCoverPage
                          ? e => {
                              e.stopPropagation();
                              handlePhotoClickForCoverPage(photo.id);
                            }
                          : undefined
                      }
                      sx={{
                        cursor: isSelectingForCoverPage ? 'pointer' : 'default',
                        position: 'relative',
                        // Hide drag indicator in selection mode
                        ...(isSelectingForCoverPage && {
                          '& .photo-drag-indicator': {
                            display: 'none !important',
                          },
                        }),
                      }}
                    >
                      <DraggablePhotoItem
                        photo={photo}
                        index={index}
                        thumbnailUrl={cbUrl}
                        localCaption={localState?.caption || ''}
                        movePhoto={handleMovePhoto}
                        onUpdateCaption={handleUpdateCaption}
                        onDelete={handleDeletePhoto}
                        onCaptionChange={handleCaptionChange}
                        onEditPhoto={onEditPhoto}
                        onRotate={handleRotate}
                        onSaveRotation={handleSaveRotationForPhoto}
                        pendingRotation={localState?.pendingRotation ?? 0}
                        isSavingRotation={savingRotationIds.has(photo.id)}
                        onMoveToNext={handleMoveToNext}
                        onMoveToPrevious={handleMoveToPrevious}
                        onMoveToBottom={handleMoveToBottom}
                        onRegisterEditTrigger={(trigger: () => void) => {
                          photoRefs.current[photo.id] = trigger;
                        }}
                        getImageAsBase64={api.getImageAsBase64}
                        isReadOnly={isReadOnly}
                        maxCaptionLength={reportType === 'magic' ? 100 : undefined}
                      />
                    </Box>
                  </Box>
                </Grid>
              );
            })}

            {/* Uploading photos displayed at the bottom */}
            {!isReadOnly && uploadingPhotos.length > 0 && (
              <>
                {uploadingPhotos.map((photo: any, index: number) => (
                  <Grid size={{ xs: 12, sm: 6, md: 12 / photosPerRow }} key={photo.id}>
                    <UploadingPhotoItem
                      uploadingPhoto={photo}
                      index={index}
                      movePhoto={moveUploadingPhoto}
                      onRemove={removeUploadingPhoto}
                    />
                  </Grid>
                ))}
              </>
            )}

            {!isReadOnly && (
              <Grid size={{ xs: 12, sm: 6, md: 12 / photosPerRow }}>
                <UploadDropzoneCard onFilesSelected={handleFilesSelected} onBeforeOpen={triggerSaveIfNeeded} />
              </Grid>
            )}
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default UploadImagesStep;
